// Tenant ticket-numbering config, loaded ONCE at the app shell (Layout) and
// shared via context so every ticket surface can format `#number` consistently
// without each one re-fetching. The raw integer is what the API stores; this
// only carries the display prefix + zero-padding width. Falls back to the
// legacy bare-integer format until (or if) the config loads.

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { getTicketNumbering } from "./api";
import { DEFAULT_TICKET_NUMBER_FORMAT, type TicketNumberFormat } from "./format";

const TicketNumberingContext = createContext<TicketNumberFormat>(DEFAULT_TICKET_NUMBER_FORMAT);

export function TicketNumberingProvider({ children }: { children: ReactNode }) {
  const [cfg, setCfg] = useState<TicketNumberFormat>(DEFAULT_TICKET_NUMBER_FORMAT);

  useEffect(() => {
    const ctrl = new AbortController();
    getTicketNumbering(ctrl.signal)
      .then((res) => setCfg({ prefix: res.numbering.prefix, padding: res.numbering.padding }))
      .catch(() => {
        /* transient failure → keep the safe bare-integer default */
      });
    return () => ctrl.abort();
  }, []);

  return <TicketNumberingContext.Provider value={cfg}>{children}</TicketNumberingContext.Provider>;
}

/** The current tenant's ticket-number display config (prefix + padding). */
export function useTicketNumberFormat(): TicketNumberFormat {
  return useContext(TicketNumberingContext);
}
